import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { ReactNode, RefObject } from "react";
import styled, { useTheme } from "styled-components";
import { ICON_SIZES } from "../icon/Icon";
import { IconButton } from "../icon/IconButton";
import { ICON_PATHS } from "../icon/IconSVGPaths";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { useBodyScrollLock } from "src/apps/hooks/useBodyScrollLock";
import { MobileModal } from "./MobileModal";
import { colors } from "@permisso/permisso-components";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  body?: ReactNode;
  primaryButton?: ReactNode;
  secondaryButton?: ReactNode;
  initialFocusRef?: RefObject<HTMLElement>;
};

export const Modal = ({
  isOpen,
  onClose,
  title,
  body,
  primaryButton,
  secondaryButton,
  initialFocusRef,
}: ModalProps) => {
  const { isDesktop, isMobile } = useIsMedia();
  const { blackColor } = useTheme();
  useBodyScrollLock(isOpen);

  const size = isDesktop ? "lg" : "sm";

  if (isMobile) {
    return (
      <MobileModal isOpen={isOpen} onClose={onClose}>
        <MobileModalContent>
          {title ? <MobileModalTitle>{title}</MobileModalTitle> : null}
          {body ? <MobileModalBody>{body}</MobileModalBody> : null}
          {primaryButton || secondaryButton ? (
            <MobileModalActions>
              {primaryButton}
              {secondaryButton}
            </MobileModalActions>
          ) : null}
        </MobileModalContent>
      </MobileModal>
    );
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} initialFocusRef={initialFocusRef} isCentered>
      <ModalOverlay>
        <StyledModalContent $isDesktop={isDesktop} $isMobile={isMobile} maxWidth={size}>
          {title ? (
            <StyledModalHeader>
              {title}
              <IconButton
                onClick={onClose}
                size={ICON_SIZES.s12}
                path={ICON_PATHS.CROSS}
                color={blackColor}
              />
            </StyledModalHeader>
          ) : null}
          {!title ? (
            <ModalCloseButton
              onClick={onClose}
              size={ICON_SIZES.s12}
              path={ICON_PATHS.CROSS}
              color={blackColor}
            />
          ) : null}
          {body ? <StyledModalBody $hasTitle={Boolean(title)}>{body}</StyledModalBody> : null}
          {primaryButton || secondaryButton ? (
            <StyledModalFooter $isDesktop={isDesktop}>
              {secondaryButton}
              {primaryButton}
            </StyledModalFooter>
          ) : null}
        </StyledModalContent>
      </ModalOverlay>
    </ChakraModal>
  );
};

const StyledModalBody = styled(ModalBody)<{ $hasTitle: boolean }>`
  padding: ${({ $hasTitle }) => ($hasTitle ? "0rem 1.5rem 1.5rem" : "1.5rem")} !important;
`;

const StyledModalHeader = styled(ModalHeader)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem !important;
`;

const ModalCloseButton = styled(IconButton)`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
`;

const MobileModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
`;

const MobileModalTitle = styled.div`
  margin: 0;
  color: ${colors.grey800};
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.5;
`;

const MobileModalBody = styled.section``;

const MobileModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledModalContent = styled(ModalContent)<{ $isDesktop: boolean; $isMobile: boolean }>`
  border-radius: ${({ $isMobile }) => ($isMobile ? "1rem 1rem 0 0" : "0.75rem")};
`;

const StyledModalFooter = styled(ModalFooter)<{ $isDesktop: boolean }>`
  gap: 12px;
  flex-direction: ${({ $isDesktop }) => ($isDesktop ? "row" : "column-reverse")};
`;
